import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import Form from 'shared/components/FormV2';
import policies from 'shared/configs/rbac/accessPolicies';
import { QueryKeys } from 'shared/constants/entityTypes';
import { MultiFields } from 'shared/constants/fields';
import useAccessControl from 'shared/hooks/misc/useAccessControl';
import { useAsyncSelect } from 'shared/hooks/misc/useAsyncSelect';
import { entityRefMap } from 'shared/utils/fields';
import {
  deferToNextTick,
  isEmptyString,
  isNonArrayObject,
  isNotNil,
} from 'shared/utils/functions';
import { useEntityDisplayName } from 'shared/hooks/misc/fields';
import { toTitleCase } from 'shared/utils/string';
import { useAsyncSelectTurnaround } from '../../hooks/misc/useAsyncSelectTurnaround';
import {
  CreatableMenu,
  CreatableModal,
  useAsyncSelectCreatable,
} from './AsyncSelectCreatable';
import { reactSelectStyle } from './styles';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value, { field, payload }) {
    const { name, fieldType, additionalAttribute } = field;
    const isMulti = MultiFields.includes(fieldType);

    if (isEmpty(value)) {
      return { [name]: isMulti ? [] : null };
    }
    if (additionalAttribute) {
      const nameMap = payload[`${name}_data_ent_name_map`] || {};
      return { [name]: nameMap };
    }
    return { [name]: value };
  },
  toServer(value, { field }) {
    const irregulars = {
      additional_customers: 'additional_customer',
    };
    const { name, fieldType, additionalAttribute } = field;
    const isMulti = MultiFields.includes(fieldType);
    const isEmpty = isEmptyString(value) || isNil(value);
    const key = additionalAttribute
      ? name
      : `${irregulars[name] || name}_${isMulti ? 'ids' : 'id'}`;

    if (isEmpty) {
      return { [key]: isMulti ? (additionalAttribute ? '' : []) : null };
    }
    if (isMulti) {
      const values = (isNonArrayObject(value) ? [value] : value).map(
        (v) => v.value
      );
      return {
        // Handle multi-select fields
        [key]: additionalAttribute ? values.join(',') : values,
      };
    }
    return { [key]: value.value }; // Handle single-select fields
  },
  defaultValue(value, { field, payload }) {
    let values;
    const {
      entityType,
      fieldType,
      fieldTypeEntityReference,
      additionalAttribute,
    } = field;
    const isEditing = !isEmpty(payload);
    const isMulti = MultiFields.includes(fieldType);
    const isCore = !additionalAttribute;
    const entityRef = entityRefMap[entityType][fieldTypeEntityReference];

    if (isEditing && isCore && isNotNil(value)) {
      values = isMulti
        ? value.reduce((acc, ent) => {
            const label = entityRef.getOptionLabel(ent);
            const value = entityRef.getOptionValue(ent);
            return { ...acc, [value]: label };
          }, {})
        : {
            [entityRef.getOptionValue(value)]: entityRef.getOptionLabel(value),
          };
    }

    const defaultValue = entityRef?.getDefaultValue?.(values || value);
    // A required field must not default to a synthetic null-valued option
    // (turnaround's "Standard"): it satisfies the frontend required rule
    // but submits `<name>_id: null`, which the backend rejects.
    if (
      field.portalRequired &&
      isNonArrayObject(defaultValue) &&
      isNil(defaultValue.value)
    ) {
      return '';
    }
    // Note: react-hook-form prefers empty defaults to be empty string.
    // https://react-hook-form.com/docs/useform#defaultValues
    return defaultValue || '';
  },
};

const AsyncSelect = React.memo(function AsyncSelect({
  name,
  control,
  required,
  setValue,
  fieldType,
  fieldTypeEntityReference = '',
  entityType = '',
  readOnly,
  isSelective,
}) {
  const {
    field: { onChange, value, ref },
  } = useController({ name, control, rules: { required } });
  const turnaroundParams = useAsyncSelectTurnaround({
    entityType,
    fieldTypeEntityReference,
  });
  const isMulti = MultiFields.includes(fieldType);
  const customStyles = reactSelectStyle(false, isMulti);
  const { getOptionLabel, getOptionValue, creatable } =
    entityRefMap[entityType][fieldTypeEntityReference] || {};
  const [createFn] = creatable?.mutation?.() || [];
  const creatableFormId = `qbench${toTitleCase(
    entityType.toLowerCase()
  )}${toTitleCase(fieldTypeEntityReference.toLowerCase())}Form`;
  const baseProps = useAsyncSelect({
    queryKey: QueryKeys[fieldTypeEntityReference],
    getOptionLabel,
    getOptionValue,
    required,
    ...(turnaroundParams && { reqParams: turnaroundParams }),
  });
  const hasCreatePermission =
    useAccessControl(creatable?.policy || policies.none) && !isSelective;
  const [entityDisplayName] = useEntityDisplayName(fieldTypeEntityReference);
  const entityLabel =
    entityDisplayName || fieldTypeEntityReference.toLowerCase();
  const { modalProps, menuProps, setCanSubmit, setSubmitted } =
    useAsyncSelectCreatable({
      formId: creatableFormId,
      buttonLabel: `Add New ${entityLabel}`,
      modalTitle: `Create New ${entityLabel}`,
    });
  const asyncPaginateProps = {
    ...baseProps,
    ...(creatable &&
      hasCreatePermission && {
        components: { Menu: (p) => <CreatableMenu {...menuProps} {...p} /> },
      }),
  };

  const submitCreatableEntity = useCallback(
    async (data) => {
      const newValue = await createFn(data);
      setSubmitted();
      deferToNextTick(() =>
        setValue(
          name,
          {
            value: getOptionValue(newValue),
            label: getOptionLabel(newValue),
          },
          { shouldValidate: true }
        )
      );
    },
    [createFn, setSubmitted, name, getOptionValue, getOptionLabel]
  );

  const nestedFormProps = useMemo(
    () => ({
      id: creatableFormId,
      entityType: fieldTypeEntityReference,
      onCanSubmit: setCanSubmit,
      onSubmit: submitCreatableEntity,
    }),
    [
      creatableFormId,
      fieldTypeEntityReference,
      setCanSubmit,
      submitCreatableEntity,
    ]
  );

  if (!fieldTypeEntityReference) return null;

  return (
    <>
      <AsyncPaginate
        inputId={name}
        inputRef={ref}
        value={value}
        styles={customStyles}
        onChange={onChange}
        isDisabled={readOnly}
        isMulti={isMulti}
        isClearable
        {...asyncPaginateProps}
      />
      <CreatableModal {...modalProps}>
        <Form {...nestedFormProps} />
      </CreatableModal>
    </>
  );
});

AsyncSelect.transform = transform;
export default AsyncSelect;
